/**
 * Iter 110 · KioskForce Admin Control Panel — layout + Dashboard tab.
 *
 * Route: `/admin/cpanel`
 *
 * Left sidebar (5 items — μόνο το Dashboard ενεργό)
 * Top header με τίτλο + Logout
 * Dashboard: 9 colored stat cards με auto-refresh κάθε 60 δευτ.
 * Προστασία: JWT + role ∈ {master, admin}. Redirect → /saas-login αλλιώς.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Megaphone,
  Settings as SettingsIcon,
  LogOut,
  RefreshCw,
  Activity,
  ShieldCheck,
  Gift,
  Clock,
  XCircle,
  Lock,
  TrendingUp,
  Calendar,
  Hourglass,
} from "lucide-react";
import AdminUsersKiosks from "@/pages/AdminUsersKiosks";
import AdminPayments from "@/pages/AdminPayments";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AUTO_REFRESH_MS = 60_000;

const MENU = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, active: true },
  { key: "users", label: "Χρήστες & Περίπτερα", icon: Users, active: true },
  { key: "payments", label: "Πληρωμές", icon: CreditCard, active: true },
  { key: "broadcast", label: "Emails / Broadcast", icon: Megaphone, active: false },
  { key: "settings", label: "Ρυθμίσεις", icon: SettingsIcon, active: false },
];

function readJwt() {
  return (
    localStorage.getItem("kf_jwt") ||
    localStorage.getItem("kiosk_jwt") ||
    localStorage.getItem("token") ||
    localStorage.getItem("jwt") ||
    ""
  );
}

function currency(n) {
  const v = Number(n || 0);
  return `€${v.toLocaleString("el-GR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// ── Stat card definitions (χρωματιστές gradient κάρτες) ────────────────────
const CARDS = [
  { key: "online_now",       label: "Online Τώρα",         emoji: "🟢", icon: Activity,     accent: "from-emerald-500 to-green-600",  ring: "ring-emerald-400/40",  kind: "int" },
  { key: "active_kiosks",    label: "Ενεργά Περίπτερα",    emoji: "✅", icon: ShieldCheck,  accent: "from-sky-500 to-cyan-600",       ring: "ring-sky-400/40",      kind: "int" },
  { key: "trials",           label: "Σε Trial",            emoji: "🎁", icon: Gift,         accent: "from-fuchsia-500 to-purple-600", ring: "ring-fuchsia-400/40",  kind: "int" },
  { key: "expiring_7d",      label: "Λήγουν σε 7 μέρες",   emoji: "⚠️",  icon: Clock,        accent: "from-amber-500 to-orange-600",   ring: "ring-amber-400/40",    kind: "int" },
  { key: "expired",          label: "Ληγμένα",             emoji: "❌", icon: XCircle,      accent: "from-rose-500 to-red-600",       ring: "ring-rose-400/40",     kind: "int" },
  { key: "locked",           label: "Κλειδωμένα",          emoji: "🔒", icon: Lock,         accent: "from-slate-600 to-slate-800",    ring: "ring-slate-400/30",    kind: "int" },
  { key: "revenue_month",    label: "Έσοδα Μήνα",          emoji: "💰", icon: TrendingUp,   accent: "from-lime-500 to-emerald-600",   ring: "ring-lime-400/40",     kind: "eur" },
  { key: "revenue_today",    label: "Έσοδα Σήμερα",        emoji: "📅", icon: Calendar,     accent: "from-teal-500 to-emerald-600",   ring: "ring-teal-400/40",     kind: "eur" },
  { key: "pending_payments", label: "Εκκρεμείς Πληρωμές",  emoji: "⏳", icon: Hourglass,    accent: "from-indigo-500 to-blue-600",    ring: "ring-indigo-400/40",   kind: "int" },
];

// ── Sidebar ────────────────────────────────────────────────────────────────
function Sidebar({ activeKey, onSelect }) {
  return (
    <aside
      className="hidden md:flex md:w-64 lg:w-72 shrink-0 flex-col border-r border-slate-800 bg-slate-950/80 backdrop-blur-xl"
      data-testid="cpanel-sidebar"
    >
      <div className="px-6 py-6 border-b border-slate-800">
        <p className="text-[10px] uppercase tracking-[0.35em] text-emerald-300/80">
          KioskForce
        </p>
        <p className="mt-1 text-lg font-black text-white">Control Panel</p>
      </div>
      <nav className="flex-1 space-y-1 p-3">
        {MENU.map((item) => {
          const Icon = item.icon;
          const isActive = item.key === activeKey;
          return (
            <button
              key={item.key}
              type="button"
              data-testid={`cpanel-nav-${item.key}`}
              disabled={!item.active}
              onClick={() => item.active && onSelect(item.key)}
              className={`group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                isActive
                  ? "bg-gradient-to-r from-emerald-500/25 to-emerald-500/10 text-white ring-1 ring-emerald-400/40"
                  : item.active
                  ? "text-slate-300 hover:bg-white/5 hover:text-white"
                  : "text-slate-500 cursor-not-allowed opacity-60"
              }`}
              title={item.active ? item.label : "Coming soon"}
            >
              <Icon className="h-4 w-4" />
              <span className="flex-1 text-left">{item.label}</span>
              {!item.active && (
                <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[9px] uppercase tracking-widest text-slate-400">
                  soon
                </span>
              )}
            </button>
          );
        })}
      </nav>
      <div className="border-t border-slate-800 px-4 py-3 text-[10px] text-slate-500">
        Made by John · Iter 111
      </div>
    </aside>
  );
}

// ── Stat card ──────────────────────────────────────────────────────────────
function StatCard({ card, value, loading }) {
  const Icon = card.icon;
  const display =
    card.kind === "eur" ? currency(value) : value === undefined || value === null ? "—" : value;
  return (
    <div
      data-testid={`cpanel-stat-${card.key}`}
      className={`relative overflow-hidden rounded-2xl border border-white/5 bg-gradient-to-br ${card.accent} p-5 text-white shadow-xl ring-1 ${card.ring} transition hover:scale-[1.015] hover:shadow-2xl`}
    >
      <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-white/10 blur-2xl" />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-white/80">
            <span className="mr-1">{card.emoji}</span>
            {card.label}
          </p>
          <p
            className={`mt-3 font-black tracking-tight ${
              card.kind === "eur" ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl"
            }`}
            data-testid={`cpanel-stat-value-${card.key}`}
          >
            {loading ? (
              <span className="inline-block h-9 w-24 animate-pulse rounded bg-white/25" />
            ) : (
              display
            )}
          </p>
        </div>
        <div className="rounded-xl bg-white/15 p-2.5 backdrop-blur-sm">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function AdminCPanel() {
  const nav = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const client = useMemo(
    () =>
      axios.create({
        baseURL: `${BACKEND_URL}/api`,
        headers: { Authorization: `Bearer ${readJwt()}` },
        timeout: 15_000,
      }),
    [],
  );

  const fetchStats = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const { data } = await client.get("/admin/stats");
        setStats(data);
        setError(null);
        setLastFetched(new Date());
      } catch (err) {
        const code = err?.response?.status;
        if (code === 401) {
          toast.error("Η συνεδρία έληξε — παρακαλώ συνδεθείτε ξανά.");
          nav("/saas-login", { replace: true });
          return;
        }
        if (code === 403) {
          setError("Δεν έχετε δικαιώματα admin για αυτή τη σελίδα.");
          return;
        }
        setError(err?.response?.data?.detail || err?.message || "Αποτυχία φόρτωσης στατιστικών.");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [client, nav],
  );

  useEffect(() => {
    if (!readJwt()) {
      nav("/saas-login", { replace: true });
      return;
    }
    fetchStats(false);
    const timer = setInterval(() => fetchStats(true), AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchStats, nav]);

  function handleLogout() {
    ["kf_jwt", "kiosk_jwt", "token", "jwt"].forEach((k) => localStorage.removeItem(k));
    toast.success("Αποσυνδεθήκατε.");
    nav("/saas-login", { replace: true });
  }

  return (
    <div
      className="min-h-screen w-full bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100"
      data-testid="admin-cpanel-page"
    >
      <div className="flex min-h-screen">
        <Sidebar activeKey={activeTab} onSelect={setActiveTab} />
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Top header */}
          <header
            className="flex items-center justify-between border-b border-slate-800 bg-slate-950/70 px-6 py-4 backdrop-blur-xl"
            data-testid="cpanel-header"
          >
            <div>
              <p className="text-[10px] uppercase tracking-[0.35em] text-emerald-300/80">
                Admin Ops
              </p>
              <h1 className="text-xl sm:text-2xl font-black text-white">
                KioskForce Control Panel
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="cpanel-refresh-btn"
                onClick={() => fetchStats(false)}
                className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-slate-200 transition hover:bg-white/10"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                Ανανέωση
              </button>
              <button
                type="button"
                data-testid="cpanel-logout-btn"
                onClick={handleLogout}
                className="flex items-center gap-2 rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-200 transition hover:bg-rose-500/20"
              >
                <LogOut className="h-3.5 w-3.5" />
                Logout
              </button>
            </div>
          </header>

          {/* Body */}
          <main className="flex-1 overflow-y-auto px-6 py-8 lg:px-10" data-testid="cpanel-body">
            {activeTab === "dashboard" ? (
              <div data-testid="cpanel-dashboard">
                <div className="mb-6 flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <h2 className="text-lg font-bold text-white">📊 Dashboard</h2>
                    <p className="text-xs text-slate-400">
                      Πλατφορμική επισκόπηση · αυτόματη ανανέωση κάθε 60 δευτ.
                    </p>
                  </div>
                  <div className="text-[11px] text-slate-500" data-testid="cpanel-last-fetched">
                    {lastFetched
                      ? `Τελευταία ενημέρωση · ${lastFetched.toLocaleTimeString("el-GR", {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })}`
                      : "…"}
                  </div>
                </div>

                {error ? (
                  <div
                    data-testid="cpanel-error"
                    className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"
                  >
                    {error}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3" data-testid="cpanel-stat-grid">
                    {CARDS.map((card) => (
                      <StatCard
                        key={card.key}
                        card={card}
                        loading={loading}
                        value={stats?.[card.key]}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : activeTab === "users" ? (
              <div data-testid="cpanel-users">
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-white">👥 Χρήστες & Περίπτερα</h2>
                  <p className="text-xs text-slate-400">
                    Πλήρης διαχείριση tenants · ενεργοποίηση, trial extension, notes.
                  </p>
                </div>
                <AdminUsersKiosks />
              </div>
            ) : activeTab === "payments" ? (
              <div data-testid="cpanel-payments">
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-white">💰 Πληρωμές</h2>
                  <p className="text-xs text-slate-400">
                    Ζωντανή παρακολούθηση εσόδων · έγκριση/απόρριψη τραπεζικών & μετρητών · auto-refresh 60δ.
                  </p>
                </div>
                <AdminPayments />
              </div>
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
}
